import React, { useState, useEffect } from 'react';
import { Upload, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import PropertyImageCarouselModal from './PropertyImageCarouselModal';
import workAnalyzerService from '../../services/workAnalyzerService';
import propertyService from '../../services/propertyService';

interface PropertyImageSectionDisplayProps {
  propertyId: string;
  imageIds: string[];
  onImageUpload: (uploadedImageIds: string[]) => void;
}

const PropertyImageSectionDisplay: React.FC<PropertyImageSectionDisplayProps> = ({
  propertyId,
  imageIds,
  onImageUpload,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageUrls, setImageUrls] = useState<string[]>([]);

  // Load signed URLs for images
  useEffect(() => {
    const fetchImageUrls = async () => {
      try {
        if(propertyId) {
            const urls = await Promise.all(
            imageIds.map((imageId) => propertyService.getPropertyImageUrl(propertyId, imageId))
            );
            setImageUrls(urls);
        }
      } catch (error) {
        console.error('Error fetching image URLs:', error);
      }
    };

    if (imageIds?.length > 0) {
      fetchImageUrls();
    }
  }, [imageIds, propertyId]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;

    setLoading(true);
    const files = Array.from(event.target.files);
    try {
      const uploadedIds: string[] = [];
      for (const file of files) {
        const uploadResult = await workAnalyzerService.upload(file);
        if (uploadResult && uploadResult.id) {
          uploadedIds.push(uploadResult.id);
        }
      }
      onImageUpload(uploadedIds);
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative text-center">
      {imageUrls && imageUrls?.length > 0 ? (
        <>
          <img
            src={imageUrls[0]} // Display the first image
            alt="Property"
            className="w-full rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow object-cover h-48"
            onClick={() => setIsModalOpen(true)}
          />
          <div className="absolute bottom-2 right-2 bg-black/50 text-white px-2 py-1 text-xs rounded-md">
            {imageUrls.length} {imageUrls.length === 1 ? 'image' : 'images'}
          </div>
          <PropertyImageCarouselModal
            imageUrls={imageUrls}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
          />
        </>
      ) : (
        <div className="w-full h-48 bg-gray-100 rounded-lg flex justify-center items-center shadow-sm">
          {loading ? (
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          ) : (
            <ImageIcon className="w-10 h-10 text-gray-300" />
          )}
        </div>
      )}
      <div className="mt-4">
        <Button
          variant="outline"
          size="sm"
          className="relative"
          disabled={loading}
        >
          <span className={loading ? "opacity-0" : "flex items-center"}>
            <Upload className="w-4 h-4 mr-2" />
            <span>{imageUrls?.length > 0 ? 'Add Images' : 'Upload Images'}</span>
          </span>
          {loading && (
            <span className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
            </span>
          )}
          <input type="file" multiple className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleImageUpload} />
        </Button>
      </div>
    </div>
  );
};

export default PropertyImageSectionDisplay;
